import { useState, useEffect } from 'react';

interface CompanyLogoState {
  logoUrl: string | null;
  loading: boolean;
  error: Error | null;
}

export const useCompanyLogo = (companyName: string, websiteUrl: string, existingLogo?: string) => {
  const [state, setState] = useState<CompanyLogoState>({
    logoUrl: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        // If we have an existing logo, try that first
        if (existingLogo) {
          const img = new Image();
          img.onload = () => {
            setState({ 
              logoUrl: existingLogo, 
              loading: false, 
              error: null 
            });
          };
          img.onerror = tryGoogleFavicon;
          img.src = existingLogo;
          return;
        }

        tryGoogleFavicon();
      } catch (error) {
        setState({ 
          logoUrl: null, 
          loading: false, 
          error: error as Error 
        });
      }
    };

    const tryGoogleFavicon = () => {
      // Only try Google favicon if we have a website URL
      if (!websiteUrl) {
        setState({ 
          logoUrl: null, 
          loading: false, 
          error: new Error('No website URL provided') 
        });
        return;
      }

      // Clean the website URL
      const domain = websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      
      // Try different Google favicon domains
      const googleDomains = ['www.google.com', 't0.gstatic.com', 't1.gstatic.com', 't2.gstatic.com', 't3.gstatic.com'];
      let attemptCount = 0;

      const tryNextDomain = () => {
        if (attemptCount >= googleDomains.length) {
          setState({ 
            logoUrl: null, 
            loading: false, 
            error: new Error('Failed to load logo from all sources') 
          });
          return;
        }

        const faviconUrl = `https://${googleDomains[attemptCount]}/s2/favicons?domain=${domain}&sz=128`;
        const img = new Image();
        
        img.onload = () => {
          setState({ 
            logoUrl: faviconUrl, 
            loading: false, 
            error: null 
          });
        };

        img.onerror = () => {
          attemptCount++;
          tryNextDomain();
        };

        img.src = faviconUrl;
      };

      tryNextDomain();
    };

    fetchLogo();
  }, [websiteUrl, existingLogo]);

  return state;
}; 